import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Cable, Cpu, MonitorCheck, Check, Server, Cloud, Building2 } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal, SectionHeading } from "../components/common/Section";
import Faq from "../components/common/Faq";
import PolicyOutcomes from "../components/diagrams/PolicyOutcomes";
import { Button } from "../components/ui/button";
import { PLATFORM_COMPONENTS, DEPLOYMENT_OPTIONS, ONBOARDING_STEPS, PLATFORM_FAQS } from "../data/content";
import { useContent } from "../cms/ContentContext";

const compIcons = [Cable, Cpu, MonitorCheck];
const deployIcons = [Server, Cloud, Building2];

export default function Platform() {
  const { content } = useContent();
  const h = content.pages?.platform || {};
  useEffect(() => {
    document.title = "Platform | norse3";
  }, []);

  return (
    <div>
      <PageHero
        eyebrow={h.eyebrow || "Platform"}
        title={h.title || "Three components. One control and evidence layer."}
        subtitle={h.subtitle || "Bifröst Relay, Aegis Engine and Heimdall Console work together to identify risk, apply your policy and keep humans in control."}
      >
        <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Link to="/book-a-demo">Book a walkthrough <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
        </Button>
      </PageHero>

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 space-y-6">
          {PLATFORM_COMPONENTS.map((c, i) => {
            const Icon = compIcons[i];
            return (
              <Reveal key={c.name} delay={i * 60}>
                <div className="rounded-lg border border-white/10 bg-navy-900/60 p-7 lg:p-9 grid lg:grid-cols-3 gap-8">
                  <div>
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/12 text-primary">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h2 className="mt-4 text-2xl font-bold text-foreground">{c.name}</h2>
                    <p className="mt-1 text-sm text-primary">{c.role}</p>
                  </div>
                  <div className="lg:col-span-2">
                    <p className="text-muted-foreground leading-relaxed">{c.desc}</p>
                    <ul className="mt-5 grid sm:grid-cols-2 gap-2.5">
                      {c.points.map((p) => (
                        <li key={p} className="flex items-start gap-2 text-sm text-foreground">
                          <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Policy outcomes"
              title="Every decision gives one clear outcome."
              subtitle="Each evaluated interaction returns a clear, machine-readable outcome and a reason. You configure which action applies to which policy."
            />
          </Reveal>
          <Reveal className="mt-10" delay={100}>
            <PolicyOutcomes />
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Deployment"
              title="Host norse3 wherever suits your business needs."
              subtitle="Hosted, private-cloud or customer-controlled options — subject to validation of your regulatory, security and data-residency requirements."
            />
          </Reveal>
          <Reveal className="mt-10 grid gap-4 lg:grid-cols-3" delay={100}>
            {DEPLOYMENT_OPTIONS.map((d, i) => {
              const Icon = deployIcons[i];
              return (
                <div key={d.name} className="rounded-xl border border-white/10 bg-navy-900/60 p-6">
                  <Icon className="h-6 w-6 text-primary" />
                  <h3 className="mt-4 text-lg font-semibold text-foreground">{d.name}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{d.desc}</p>
                </div>
              );
            })}
          </Reveal>
          <Reveal className="mt-6" delay={140}>
            <p className="text-sm text-muted-foreground max-w-3xl">
              Access, retention and audit settings are configurable by your administrators.
              Customer conversations, personal data and incidents are not commingled across
              tenants by default. We do not make “zero access” claims.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading
              eyebrow="Onboarding"
              title="We'll be right there with you, every step of the way."
              subtitle="Discovery → policy mapping → integration → testing → launch → monitoring — validated with your teams at each stage."
            />
          </Reveal>
          <Reveal className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" delay={100}>
            {ONBOARDING_STEPS.map((s, i) => (
              <div key={s.step} className="rounded-xl border border-white/10 bg-navy-900/60 p-6">
                <span className="text-xs font-mono text-primary/70">0{i + 1}</span>
                <h3 className="mt-2 text-lg font-semibold text-foreground">{s.step}</h3>
                <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 lg:py-20 bg-navy-950/40">
        <div className="mx-auto max-w-3xl px-5 lg:px-8">
          <Reveal>
            <SectionHeading eyebrow="FAQs" title="Accountability, policy and evidence." align="center" />
          </Reveal>
          <Reveal className="mt-8" delay={100}><Faq items={PLATFORM_FAQS} /></Reveal>
        </div>
      </section>
    </div>
  );
}
